import React, {useEffect, useState} from 'react'
import ReservationFeedbackModal from './ReservationFeedbackModal'

function FormReservation({
    creneauSelectionne,
    formData,
    dateSelectionnee,
    onSubmitReservation,
    initialClientData,
    initialCommentaire,
    submitLabel = "Réserver",
}) {
    const [nom, setNom] = useState("");
    const [prenom, setPrenom] = useState("");
    const [email, setEmail] = useState("");
    const [telephone, setTelephone] = useState("");
    const [commentaire, setCommentaire] = useState("");
    const [errorsForm, setErrorsForm] = useState({});
    const [loadingReservation, setLoadingReservation] = useState(false);
    const [feedbackType, setFeedbackType] = useState("error");
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const [feedbackDetails, setFeedbackDetails] = useState("");
    const [feedbackContext, setFeedbackContext] = useState(null);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [cgvAccepted, setCgvAccepted] = useState(false);

    useEffect(() => {
        if (!initialClientData) return;
        setNom(initialClientData.nom || "");
        setPrenom(initialClientData.prenom || "");
        setEmail(initialClientData.email || "");
        setTelephone(initialClientData.telephone || "");
    }, [initialClientData]);

    useEffect(() => {
        if (initialCommentaire === undefined || initialCommentaire === null) return;
        setCommentaire(initialCommentaire);
    }, [initialCommentaire]);

    const validateForm = () => {
        const e = {};
        if (!nom.trim()) e.nom = "Nom requis";
        if (!prenom.trim()) e.prenom = "Prénom requis";
        if (!email.trim()) e.email = "Email requis";
        else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = "Email invalide";
        if (!telephone.trim()) e.telephone = "Téléphone requis";
        else if (!/^\+?\d{6,15}$/.test(telephone)) e.telephone = "Téléphone invalide";
        if (!cgvAccepted) e.cgv = "Veuillez accepter les CGV pour réserver";
        setErrorsForm(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmitReservation = async (ev) => {
        ev.preventDefault();
        setFeedbackMessage("");
        setFeedbackDetails("");
        setFeedbackContext(null);
        setIsFeedbackOpen(false);
        if (!validateForm()) return;

        // récupérer l'identifiant du créneau sélectionné
        const creneauId =
            creneauSelectionne ||
            (formData && (formData.creneauId || formData.creneau || formData.id));

        if (!creneauId) {
            setFeedbackType("error");
            setFeedbackMessage("Veuillez sélectionner un créneau avant de réserver.");
            setIsFeedbackOpen(true);
            return;
        }
        const clientData = {
            nom: nom.trim(),
            prenom: prenom.trim(),
            email: email.trim(),
            telephone: telephone.trim(),
        };

        if (typeof onSubmitReservation !== "function") {
            console.warn("Aucun gestionnaire de soumission défini pour la réservation.");
            return;
        }

        setLoadingReservation(true);
        try {
            const result = await onSubmitReservation({
                client_data: clientData,
                commentaire: commentaire.trim(),
            });

            if (result?.success) {
                setFeedbackType("success");
                setFeedbackMessage("");
                setFeedbackDetails("");
                setFeedbackContext({
                    participants: Number(formData?.participants) || 1,
                    atelierLabel: formData?.atelierNom || "",
                    dateLabel: selectedDateObj
                        ? selectedDateObj.toLocaleDateString("fr-FR", {
                              weekday: "long",
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                          })
                        : "",
                    timeLabel: formatHeureAffichee(heureCreneauSelectionne) || "",
                    email: clientData.email,
                });
                setIsFeedbackOpen(true);
                setNom("");
                setPrenom("");
                setEmail("");
                setTelephone("");
                setCommentaire("");
                setErrorsForm({});
            } else if (result?.message) {
                setFeedbackType("error");
                setFeedbackMessage(result.message);
                setFeedbackDetails("");
                setFeedbackContext(null);
                setIsFeedbackOpen(true);
            } else {
                setFeedbackType("error");
                setFeedbackMessage("");
                setFeedbackDetails("");
                setFeedbackContext(null);
                setIsFeedbackOpen(true);
            }
        } catch (err) {
            setFeedbackType("error");
            setFeedbackMessage("");
            setFeedbackDetails(
                "Erreur lors de la préparation de la réservation : " + (err.message || err)
            );
            setFeedbackContext(null);
            setIsFeedbackOpen(true);
        } finally {
            setLoadingReservation(false);
        }
    };

    const selectedDateObj = formData?.date
        ? new Date(formData.date)
        : dateSelectionnee
        ? new Date(dateSelectionnee)
        : null;

    const jourSelectionne = selectedDateObj
        ? selectedDateObj.toLocaleDateString("fr-FR", {
              weekday: "long",
              day: "2-digit",
              month: "long",
          })
        : null;

    const formatHeureAffichee = (value) => {
        if (value === null || value === undefined) return null;
        if (typeof value === "number" && Number.isFinite(value)) {
            return `${String(value).padStart(2, "0")}h00`;
        }
        if (typeof value === "string") {
            const [hours = "00", minutes = "00"] = value.split(":");
            return `${hours.padStart(2, "0")}h${minutes.padStart(2, "0")}`;
        }
        return null;
    };

    const heureCreneauSelectionne =
        formData?.creneau?.creneau_heure_debut ??
        formData?.creneau?.heure_debut ??
        formData?.creneau?.heure ??
        null;

  return (
    <div className='bg-white rounded-lg shadow-lg shadow-slate-600 p-5 mb-6'>
        <form onSubmit={handleSubmitReservation} className="flex flex-col gap-6 w-full items-stretch md:flex-row md:items-start">
        <div className='flex flex-col gap-4 w-full md:w-1/2 md:m-3'>
            <div className='mb-4'>
                <label className="block text-sm font-medium text-orange-600">Nom</label>
                <input value={nom} onChange={(e) => setNom(e.target.value)} className="mt-1 w-full border-b border-orange-400 px-2 py-1" />
                {errorsForm.nom && <div className="text-red-600 text-sm">{errorsForm.nom}</div>}
            </div>

            <div className='mb-4'>
                <label className="block text-sm font-medium text-orange-600">Prénom</label>
                <input value={prenom} onChange={(e) => setPrenom(e.target.value)} className="mt-1 w-full  border-b border-orange-400 px-2 py-1" />
                {errorsForm.prenom && <div className="text-red-600 text-sm">{errorsForm.prenom}</div>}
            </div>

            <div className='mb-4'>
                <label className="block text-sm font-medium text-orange-600">Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border-b border-orange-400 px-2 py-1" />
                {errorsForm.email && <div className="text-red-600 text-sm">{errorsForm.email}</div>}
            </div>

            <div className='mb-4'>
                <label className="block text-sm font-medium text-orange-600">Téléphone</label>
                <input value={telephone} onChange={(e) => setTelephone(e.target.value)} className="mt-1 w-full border-b border-orange-400 px-2 py-1" />
                {errorsForm.telephone && <div className="text-red-600 text-sm">{errorsForm.telephone}</div>}
            </div>  
            <div className='mb-4'>
                <label className="block text-sm font-medium text-orange-600">Commentaire</label>
                <textarea
                    value={commentaire}
                    onChange={(e) => setCommentaire(e.target.value)}
                    rows={3}
                    className="mt-1 w-full border border-orange-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="Informations complémentaires (optionnel)"
                />
            </div>  
        </div>
        <div className='flex flex-col gap-4 w-full md:w-1/2 md:my-auto md:mx-10'>
            {formData?.creneau && (
                <div className="p-3 border border-orange-200 rounded-lg bg-orange-50 text-orange-700">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-1">
                        Créneau sélectionné
                    </p>
                    <p className="text-base font-medium">
                        {jourSelectionne ? `${jourSelectionne} - ` : ""}
                        {formatHeureAffichee(heureCreneauSelectionne) || "--"}
                    </p>
                </div>
            )}
            <div >
                <p className="text-sm text-gray-700 mb-5 ml-6">
                    Vous ne payez rien à cette étape. Le prix sera en fonction de la ou les pièce(s) que vous choisissez 
                    de peindre. Le prix des pièces démarre à 12€ et jusqu'à 60€.
                </p>
                <p className="text-sm text-gray-700 mb-5 ml-6">
                    Les céramiques sont à récupérer chez Mood sous 2 à 3 semaines après leur réalisation. 
                </p>
                <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 text-orange-600 border-orange-300 rounded focus:ring-orange-500"
                        checked={cgvAccepted}
                        onChange={(e) => setCgvAccepted(e.target.checked)}
                    />
                    <span>
                        En confirmant votre reservation, vous reconnaissez avoir pris connaissance et accepter les{" "}
                        <a className='text-orange-600 underline' href='/cgv'>conditions générales de vente</a>
                    </span>
                </label>
                {errorsForm.cgv && <div className="text-red-600 text-sm mt-1">{errorsForm.cgv}</div>}
            </div>
            <div className="pt-2 md:m-auto">
                <button type="submit" disabled={loadingReservation} className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-800">
                {loadingReservation ? "Envoi..." : submitLabel}
                </button>
            </div>

            <ReservationFeedbackModal
                isOpen={isFeedbackOpen}
                type={feedbackType}
                message={feedbackMessage}
                detailsMessage={feedbackDetails}
                context={feedbackContext}
                onClose={() => setIsFeedbackOpen(false)}
            />
        </div>
        </form>
    </div>
  )
}

export default FormReservation;
